import { Button } from '@/components/ui/button';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { type ReactNode, useCallback, useRef } from 'react';
import type { FeatureModalImage } from './features-image-modal';
import { FeatureSection } from './features-section';
import { UnderlinedText } from '@/components/ui/underline-text';

type FeaturesContentProps = {
  onOpenImage: (image: FeatureModalImage) => void;
};

function AiText({ children }: { children: ReactNode }) {
  return (
    <span className="bg-[linear-gradient(#eee_-20%,#ed630f_100%)] bg-clip-text font-bold text-transparent">
      {children}
    </span>
  );
}

function CheckItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex w-full items-center gap-2">
      <svg
        aria-hidden="true"
        className="text-orange h-4 w-4 shrink-0"
        fill="none"
        viewBox="0 0 16 16"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 8.2L6.2 11.4L13 4.6"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
      <p className="w-full">{children}</p>
    </li>
  );
}

function StoreBadgeLink({ alt, href, src }: { alt: string; href: string; src: string }) {
  return (
    <a href={href} rel="noreferrer" target="_blank">
      <Image
        alt={alt}
        className="mr-3 mb-3 w-40 cursor-pointer transition-opacity duration-150 hover:opacity-30"
        height={48}
        src={src}
        width={160}
      />
    </a>
  );
}

export default function FeaturesContent({ onOpenImage }: FeaturesContentProps) {
  const refs = {
    section: useRef(null),
    title: useRef(null),
    subtitle: useRef(null),
    blocks: useRef<HTMLDivElement[]>([]),
  };

  const setBlockRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (el) {
        refs.blocks.current[index] = el;
      }
    },
    [],
  );

  useGSAP(() => {
    if (!refs.section.current) return;

    let isCancelled = false;
    let frameId: number | undefined;

    const scheduleRefresh = () => {
      if (isCancelled) return;
      frameId = window.requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    };

    const timeoutIds = [150, 500, 1200].map((delay) => window.setTimeout(scheduleRefresh, delay));

    const images = Array.from((refs.section.current as HTMLElement).querySelectorAll('img'));

    Promise.allSettled(
      images.map(async (image) => {
        if (image.complete) {
          try {
            return await image.decode?.();
          } catch {
            return undefined;
          }
        }
        return new Promise<void>((resolve) => {
          const done = () => {
            image.removeEventListener('load', done);
            image.removeEventListener('error', done);
            resolve();
          };
          image.addEventListener('load', done, { once: true });
          image.addEventListener('error', done, { once: true });
        });
      }),
    ).then(() => {
      scheduleRefresh();
    });

    return () => {
      isCancelled = true;
      timeoutIds.forEach((id) => window.clearTimeout(id));
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  useGSAP(
    () => {
      if (!refs.section.current || !refs.title.current || !refs.subtitle.current) return;

      const splitTitle = new SplitText(refs.title.current, {
        mask: 'lines',
        type: 'lines',
      });
      const splitSubtitle = new SplitText(refs.subtitle.current, {
        mask: 'lines',
        type: 'lines',
      });

      gsap
        .timeline({
          defaults: {
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          scrollTrigger: {
            invalidateOnRefresh: true,
            start: 'top 60%',
            toggleActions: 'play none none reverse',
            trigger: refs.section.current,
          },
        })
        .from(splitTitle.lines, {
          stagger: MOTION.staggerLines,
          yPercent: MOTION.yPercent,
        })
        .from(
          splitSubtitle.lines,
          {
            stagger: MOTION.staggerLines,
            yPercent: MOTION.yPercent,
          },
          '<+0.15',
        );

      refs.blocks.current.forEach((block) => {
        if (!block) return;
        gsap.from(block, {
          autoAlpha: 0,
          duration: MOTION.durationMd,
          ease: MOTION.ease,
          scrollTrigger: {
            trigger: block,
            invalidateOnRefresh: true,
            start: 'top 80%',
            // markers: true,
            toggleActions: 'play none none reverse',
          },
          y: MOTION.y,
        });
      });
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section mx-auto flex flex-col items-center gap-12 px-6 py-16 md:px-8"
      id="fonctionnalites"
    >
      <div className="flex w-full flex-col items-start gap-2">
        <h2 ref={refs.title}>Toutes les fonctionnalités pour gérer votre cabinet</h2>

        <p ref={refs.subtitle} aria-hidden="true" className="text-dark-blue w-full">
          Conçu avec et pour les avocats, zLawyer propose une solution complète, intuitive et
          adaptée à toutes les structures.
        </p>

        <p className="sr-only">
          Conçu avec et pour les avocats, zLawyer propose une solution complète, intuitive et
          adaptée à toutes les structures.
        </p>
      </div>

      <div className="flex w-full flex-col gap-12 md:gap-20 md:px-8">
        <div ref={setBlockRef(0)}>
          <FeatureSection
            imagePosition="right"
            image={{
              alt: 'illustration de la fonctionnalité facturation',
              height: 519,
              src: withBasePath('/images/features/features9.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Facturez simplement.</UnderlinedText> Pilotez précisément.
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>zLawyer sécurise votre facturation et optimise votre gestion financière.</p>

            <ul className="flex flex-col gap-1">
              <CheckItem>Facturation au temps passé ou au forfait</CheckItem>
              <CheckItem>Gestion des frais et débours</CheckItem>
              <CheckItem>Tarifs personnalisables</CheckItem>
              <CheckItem>Majorations et minorations</CheckItem>
              <CheckItem>Suivi financier centralisé</CheckItem>
              <CheckItem>Relance des impayés simplifiée</CheckItem>
            </ul>
            <p className="mt-2">
              Une facturation fluide, rapide et adaptée aux exigences des cabinets d&apos;avocats.
            </p>

            <Button className="mt-4 w-fit" href="/contact" variant="transparent">
              Je souhaite une démonstration
            </Button>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(1)}>
          <FeatureSection
            image={{
              alt: 'illustration de la fonctionnalité pilotez votre activité',
              height: 539,
              src: withBasePath('/images/features/features4.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Pilotez votre activité</UnderlinedText> en toute simplicité
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>Centralisez vos informations, suivez votre activité et valorisez chaque minute.</p>
            <p>
              <span className="font-bold">Vos dossiers, immédiatement accessibles.</span> Retrouvez
              en un instant toutes les informations essentielles de vos dossiers, centralisées dans
              une interface claire et intuitive.{' '}
              <span className="font-bold">Moins de recherche, plus d’efficacité.</span>
            </p>
            <p>
              <span className="font-bold">Votre activité, en un coup d&apos;oeil.</span> Retrouvez
              sur votre tableau de bord les informations essentielles pour suivre et piloter votre
              cabinet.
            </p>
            <p>
              <span className="font-bold">Chaque minute devient facturable.</span> Transformez
              facilement votre temps passé sur chaque dossier en facturation fiable et maîtrisée.
            </p>
            <Button className="mt-4 w-fit" href="/contact" variant="transparent">
              Je souhaite une démonstration
            </Button>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(2)}>
          <FeatureSection
            imageClassName="object-contain md:h-96 lg:w-fit"
            imageFramed={false}
            imagePosition="right"
            image={{
              alt: "illustration de l'application mobile",
              height: 647,
              src: withBasePath('/images/features/features2.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Application mobile</UnderlinedText> zLawyer
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              <span className="font-bold">Votre cabinet, partout avec vous.</span>
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Saisie des temps en mobilité</CheckItem>
              <CheckItem>Consultation hors ligne</CheckItem>
              <CheckItem>Dossiers toujours disponibles</CheckItem>
              <CheckItem>Indicateurs clés à portée de main</CheckItem>
            </ul>
            <p>Ne perdez plus aucune information, où que vous soyez.</p>

            <div className="flex flex-wrap">
              <StoreBadgeLink
                alt="logo de téléchargement vers l'Apple Store"
                href="https://apps.apple.com/fr/app/zlawyer/id1174916635?mt=8"
                src={withBasePath('/images/logos/badgeAppStore.svg')}
              />
              <StoreBadgeLink
                alt="logo de téléchargement vers le Play Store"
                href="https://play.google.com/store/apps/details?id=com.zelesolution.zlawyer&hl=fr&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                src={withBasePath('/images/logos/badgePlayStore.svg')}
              />
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(3)}>
          <FeatureSection
            image={{
              alt: 'illustration de la fonctionnalité gestion documentaire',
              height: 481,
              src: withBasePath('/images/features/features3.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Gestion documentaire</UnderlinedText> sans contrainte
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              zLawyer simplifie l&apos;organisation de vos documents tout en respectant vos
              exigences de confidentialité.
            </p>
            <p>Vos documents restent sur votre environnement (OneDrive, SharePoint...)</p>
            <div className="flex w-full flex-col gap-2 py-2">
              <CheckItem>Organisation simple par dossier</CheckItem>
              <CheckItem>Accès immédiat</CheckItem>
              <CheckItem>Intégration fluide</CheckItem>
            </div>
            <p>
              <span className="font-bold">
                Aucune migration. Aucun surcoût. 100 % maîtrise de vos données.
              </span>
            </p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(4)}>
          <FeatureSection
            imagePosition="right"
            image={{
              alt: 'illustration de la fonctionnalité dossiers',
              height: 539,
              src: withBasePath('/images/features/features7.webp'),
              width: 863,
            }}
            title={
              <>
                L&apos;<AiText>IA</AiText> au service de votre expertise
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              Avec <span className="font-bold">Zéphyr</span>, accédez à une IA générative conçue
              pour les avocats.
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Recherche</CheckItem>
              <CheckItem>Rédaction</CheckItem>
              <CheckItem>Analyse</CheckItem>
            </ul>
            <p>Gagnez en rapidité et en efficacité au quotidien.</p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(5)}>
          <FeatureSection
            imagePosition="left"
            image={{
              alt: 'illustration de la fonctionnalité accès client',
              height: 519,
              src: withBasePath('/images/features/features8.webp'),
              width: 863,
            }}
            title={
              <>
                Donnez à vos clients <UnderlinedText>un accès à leur dossier</UnderlinedText> sur le
                web
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              Ce service vous permet de disposer d&apos;un espace à vos couleurs sur le Web afin de
              permettre aux clients de votre choix d&apos;accéder à certaines informations de leurs
              dossiers.
            </p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(6)}>
          <FeatureSection
            id="facturation-electronique"
            imagePosition="right"
            image={{
              alt: 'illustration de la fonctionnalité facturation électronique',
              height: 519,
              src: withBasePath('/images/features/features10.webp'),
              width: 863,
            }}
            title={
              <span className="text-pretty">
                <UnderlinedText>Facturation électronique</UnderlinedText> conforme
              </span>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              <span className="font-bold">Anticipez la réforme en toute sérénité.</span>
            </p>
            <p>
              Notre solution répond aux exigences de la facturation électronique et s&apos;inscrit
              dans le cadre des obligations réglementaires en vigueur.
            </p>
            <p>
              La facturation électronique est entièrement intégrée à votre environnement de travail
              :
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Conformité aux obligations légales</CheckItem>
              <CheckItem>Aucun changement d&apos;outil ou d&apos;organisation</CheckItem>
              <CheckItem>Gestion centralisée de vos dossiers et de votre facturation</CheckItem>
              <CheckItem>Interface unique, simple et intuitive</CheckItem>
            </ul>
            <p>Une transition fluide, sans rupture dans votre organisation.</p>
            <div className="mt-4 flex flex-wrap items-center justify-start gap-4">
              <Button href="/facturation-electronique-et-zlawyer" variant="fill">
                Notre solution à la réforme
              </Button>
              <Button href="/facturation-electronique" variant="transparent">
                Comprendre la réforme
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(7)}>
          <FeatureSection
            title={<UnderlinedText>Suivi des performances</UnderlinedText>}
            image={{
              alt: 'illustration de la fonctionnalité IA Zéphyr',
              height: 519,
              src: withBasePath('/images/features/features1.webp'),
              width: 863,
            }}
            onOpenImage={onOpenImage}
          >
            <p>
              <span className="font-bold">Pilotez votre cabinet avec précision.</span>
            </p>
            <p>
              Avec zLawyer, gardez le contrôle sur la performance financière de votre cabinet
              d&apos;avocats. Accédez à des indicateurs clés et des rapports détaillés pour suivre
              l&apos;activité de chaque dossier et comprendre la rentabilité de votre cabinet.
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Analyse de rentabilité</CheckItem>
              <CheckItem>Suivi des dossiers</CheckItem>
              <CheckItem>Indicateurs clés</CheckItem>
              <CheckItem>Aide à la décision</CheckItem>
            </ul>
            <p>Transformez vos données en décisions concrètes.</p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(8)}>
          <FeatureSection
            imagePosition="right"
            image={{
              alt: 'illustration de la fonctionnalité rapprochement bancaire',
              height: 519,
              src: withBasePath('/images/features/features12.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Rapprochement bancaire</UnderlinedText> automatisé
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              Automatisez votre rapprochement bancaire et gardez une vision claire de votre
              trésorerie.
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Synchronisation bancaire</CheckItem>
              <CheckItem>Lettrage intelligent des factures</CheckItem>
              <CheckItem>Suivi en temps réel</CheckItem>
              <CheckItem>Zéro saisie manuelle</CheckItem>
            </ul>
            <p>Gagnez du temps et fiabilisez votre trésorerie.</p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(10)}>
          <FeatureSection
            image={{
              alt: "illustration de la fonctionnalité essentielle de l'application",
              height: 519,
              src: withBasePath('/images/features/features11.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>MYzLawyer</UnderlinedText> : votre tableau de bord
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              Ce{' '}
              <span className="font-bold">
                tableau de bord entièrement personnalisable pour avocats
              </span>{' '}
              centralise les informations essentielles de votre activité.
            </p>
            <p>Dès votre connexion, visualisez en un coup d&apos;oeil :</p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Échéances et tâches</CheckItem>
              <CheckItem>Dossiers à facturer</CheckItem>
              <CheckItem>Factures à relancer</CheckItem>
              <CheckItem>Indicateurs clés</CheckItem>
            </ul>
            <p>Un pilotage simple, clair et efficace.</p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>

        <div ref={setBlockRef(11)}>
          <FeatureSection
            imagePosition="right"
            image={{
              alt: 'illustration de la fonctionnalité tamponnage numérique',
              height: 519,
              src: withBasePath('/images/features/features13.webp'),
              width: 863,
            }}
            title={
              <>
                <UnderlinedText>Tamponnage numérique</UnderlinedText> des pièces
              </>
            }
            onOpenImage={onOpenImage}
          >
            <p>
              Simplifiez la préparation de vos dossiers de plaidoirie grâce au{' '}
              <span className="font-bold">tamponnage numérique intégré à zLawyer.</span>
            </p>
            <p>
              Vos pièces sont automatiquement numérotées et organisées pour garantir des dossiers
              clairs, structurés et conformes aux pratiques des juridictions.
            </p>
            <ul className="flex flex-col gap-1">
              <CheckItem>Numérotation automatique</CheckItem>
              <CheckItem>Dossiers structurés</CheckItem>
              <CheckItem>Gain de temps</CheckItem>
              <CheckItem>Fiabilité renforcée</CheckItem>
            </ul>
            <p>Préparez vos dossiers plus rapidement et plaidez en toute sérénité.</p>
            <div className="mt-4">
              <Button href="/contact" variant="transparent">
                Je souhaite une démonstration
              </Button>
            </div>
          </FeatureSection>
        </div>
      </div>
    </section>
  );
}
